import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { useAuth } from "@/hooks/auth/useAuth";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Turnstile } from "@marsidev/react-turnstile";
import { useState } from "react";

interface SignInFormData {
  email: string;
  password: string;
}

export function SignInForm() {
  const { signIn } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SignInFormData>();

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const signInMutation = useMutation({
    mutationFn: async (data: SignInFormData) => signIn(data.email, data.password, turnstileToken),
    onSuccess: (result) => {
      if (result.error) {
        if (result.error.code === "captcha_failed") {
          toast.error("Échec de la vérification CAPTCHA", {
            description: "Veuillez réessayer.",
          });
          setTurnstileToken(null);
        } else {
          toast.error("Échec de la connexion");
        }
        return;
      }
      toast.success("Connexion réussie", {
        description: "Bienvenue sur Sylvain !",
      });
      reset();
    },
    onError: () => {
      toast.error("Une erreur inattendue s'est produite.");
    },
  });

  const loading = signInMutation.isPending;

  const onSubmit = (data: SignInFormData) => {
    if (!turnstileToken) {
      toast.error("Veuillez compléter la vérification Turnstile");
      return;
    }
    signInMutation.mutate(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="votre@email.com"
          {...register("email", {
            required: "Email requis",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Email invalide",
            },
          })}
          className={errors.email ? "border-red-500" : ""}
        />
        {errors.email && (
          <p className="text-sm text-red-500">{errors.email.message}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="password">Mot de passe</Label>
        <PasswordInput
          id="password"
          placeholder="••••••••"
          error={!!errors.password}
          {...register("password", {
            required: "Mot de passe requis",
            minLength: {
              value: 6,
              message: "Le mot de passe doit contenir au moins 6 caractères",
            },
          })}
        />
        {errors.password && (
          <p className="text-sm text-red-500">{errors.password.message}</p>
        )}
      </div>
      
      <div className="flex w-full justify-center">
        <Turnstile
          siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
          onSuccess={setTurnstileToken}
          onExpire={() => setTurnstileToken(null)}
          onError={() => setTurnstileToken(null)}
          options={{
            theme: "light",
          }}
        />
      </div>
      
      <div className="flex justify-end">
        <Button
          asChild
          variant="link"
          className="px-0 text-sm text-indigo-600"
          tabIndex={0}
        >
          <Link to="/recover-password">Mot de passe oublié&nbsp;?</Link>
        </Button>
      </div>
      <Button type="submit" className="w-full" disabled={loading || !turnstileToken}>
        {signInMutation.isPending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Connexion...
          </>
        ) : (
          "Se connecter"
        )}
      </Button>
    </form>
  );
} 